import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { pietersGasthuisObstacles } from './sintPietersgasthuis';
import { leprozenhuisObstacles } from './leprozenhuis';
import { doelenObstacles } from './doelenHalls';
import { paulusConventObstacles } from './paulusbroedersConvent';
import { waterfrontDefenceObstacles } from './waterfrontDefences';
import { waterGateObstacles } from './waterGate';
import { damObstacles } from './dam';
import { marketStallObstacles } from './marketStalls';
import { monasteryObstacles } from './monastery';
import { scafferijObstacles } from './scafferij';
import { lastageGardenShedObstacles } from './lastageGardenShed';
import { oudeKerkObstacles } from './oudeKerk/oudeKerkObstacles';
import { nieuweBrugWestTowerFootprint } from './nieuweBrugWestTower';
import { agnietenConventObstacles } from './agnietenConvent';
import { bethanienObstacles } from './bethanienConvent';
import { minderbroedersObstacles } from './minderbroeders';
import { cellezustersObstacles } from './cellezustersPlan';
import { prinsenhofObstacles } from './prinsenhofConventsPlan';
import { ursulaObstacles } from './ursulaPlan';
import { oudeNonnenObstacles } from './oudeNonnenPlan';
import { nieuweNonnenObstacles } from './nieuweNonnenPlan';
import { sintMariaObstacles } from './sintMariaPlan';
import { cellebroedersObstacles } from './cellebroedersPlan';
import { hasMagdalenaBarbaraPlan, magdalenaBarbaraObstacles } from './magdalenaBarbaraPlan';
import { sintClaraObstacles } from './sintClaraPlan';
import { sintMargrietObstacles } from './sintMargrietPlan';
import { sintGeertruidObstacles } from './sintGeertruidPlan';
import { sintLucienObstacles } from './sintLucienPlan';
import { kartuizersObstacles } from './kartuizersStructures';
import { lucienerfObstacles } from './lucienerfPlan';
import { sintPieterskapelFoundations } from './sintPieterskapelPlan';
import { stadhuisGroundedRings, stadhuisPlanOf } from './stadhuis/stadhuisPlan';
import { heiligewegClarissenObstacles } from './heiligewegClarissenPlan';

/** Collision follows actual grounded components; gates, courts and market aisles stay open. */
export function landmarkObstacleRings(landmark: Landmark): readonly Ring[] {
  if (landmark.id === 'landmark-kartuizerklooster') return kartuizersObstacles(landmark);
  if (landmark.id === 'landmark-clarissen') return heiligewegClarissenObstacles(landmark);
  if (landmark.id === 'landmark-lucienerf-carpenter') return lucienerfObstacles(landmark);
  if (landmark.id === 'landmark-oude-stadhuis')
    return stadhuisGroundedRings(stadhuisPlanOf(landmark));
  if (landmark.id === 'landmark-sint-pieterskapel') return sintPieterskapelFoundations(landmark);
  if (landmark.id === 'landmark-sint-lucien') return sintLucienObstacles(landmark);
  if (landmark.id === 'landmark-sint-geertruid') return sintGeertruidObstacles(landmark);
  if (landmark.id === 'landmark-sint-margriet') return sintMargrietObstacles(landmark);
  if (landmark.id === 'landmark-sint-clara') return sintClaraObstacles(landmark);
  if (hasMagdalenaBarbaraPlan(landmark)) return magdalenaBarbaraObstacles(landmark);
  if (landmark.id === 'landmark-cellebroeders') return cellebroedersObstacles(landmark);
  if (landmark.id === 'landmark-sint-maria') return sintMariaObstacles(landmark);
  if (landmark.id === 'landmark-nieuwe-nonnen') return nieuweNonnenObstacles(landmark);
  if (landmark.id === 'landmark-oude-nonnen') return oudeNonnenObstacles(landmark);
  if (landmark.id === 'landmark-sint-ursula') return ursulaObstacles(landmark);
  if (['landmark-sint-cecilia', 'landmark-sint-catharina'].includes(landmark.id))
    return prinsenhofObstacles(landmark);
  if (landmark.id === 'landmark-cellezusters') return cellezustersObstacles(landmark);
  if (landmark.id === 'landmark-minderbroeders') return minderbroedersObstacles(landmark);
  if (landmark.id === 'landmark-bethanien') return bethanienObstacles(landmark);
  if (landmark.id === 'landmark-agnieten-convent') return agnietenConventObstacles(landmark);
  if (landmark.id === 'landmark-nieuwe-brug-west-tower')
    return [nieuweBrugWestTowerFootprint(landmark)];
  if (landmark.id === 'landmark-oude-kerk') return oudeKerkObstacles(landmark);
  if (landmark.id === 'landmark-lastage-garden-shed') return lastageGardenShedObstacles(landmark);
  if (landmark.id === 'landmark-scafferij-baerdsehuys') return scafferijObstacles(landmark);
  return (
    (landmark.id === 'landmark-sint-pietersgasthuis'
      ? pietersGasthuisObstacles(landmark)
      : landmark.id === 'landmark-leprozenhuis'
        ? leprozenhuisObstacles(landmark)
        : landmark.id === 'landmark-paulusbroederskapel'
          ? paulusConventObstacles(landmark)
          : ['landmark-handboogdoelen', 'landmark-voetboogdoelen'].includes(landmark.id)
            ? doelenObstacles(landmark)
            : waterfrontDefenceObstacles(landmark)) ??
    (landmark.kind === 'waterGate'
      ? waterGateObstacles(landmark)
      : landmark.kind === 'dam'
        ? damObstacles(landmark)
        : landmark.kind === 'marketStalls'
          ? marketStallObstacles(landmark)
          : landmark.kind === 'monastery'
            ? monasteryObstacles(landmark)
            : [landmark.footprint])
  );
}
